import React from 'react';

/**
 * Queue Health Indicator Component
 * Displays overall queue system health status
 */
const QueueHealthIndicator = ({ data, theme }) => {
  if (!data) {
    return null;
  }

  const getHealthStatus = (health) => {
    switch (health) {
      case 'healthy':
        return {
          color: 'text-green-500',
          bgColor: 'bg-green-100',
          icon: '✅',
          label: 'Healthy'
        };
      case 'warning':
        return {
          color: 'text-yellow-500',
          bgColor: 'bg-yellow-100',
          icon: '⚠️',
          label: 'Warning'
        };
      case 'critical':
        return {
          color: 'text-red-500',
          bgColor: 'bg-red-100',
          icon: '🚨',
          label: 'Critical'
        };
      default:
        return {
          color: theme === 'dark' ? 'text-gray-400' : 'text-gray-500',
          bgColor: theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100',
          icon: '❓',
          label: 'Unknown'
        };
    }
  };

  const overallHealth = data.overallHealth || 'unknown';
  const healthStatus = getHealthStatus(overallHealth);
  const queueStatuses = data.queueStatuses || {};
  const alerts = data.activeAlerts || [];

  return (
    <div className={`${
      theme === 'dark' 
        ? 'bg-gray-800 border-gray-700' 
        : 'bg-white border-gray-200'
    } rounded-lg shadow border p-6`}>
      <div className="flex items-center justify-between mb-6">
        <h3 className={`text-lg font-semibold ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>
          Queue System Health
        </h3>
        
        <div className="flex items-center space-x-3">
          <span className="text-2xl">{healthStatus.icon}</span>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            healthStatus.bgColor
          } ${healthStatus.color}`}>
            {healthStatus.label}
          </span>
        </div>
      </div>

      {/* Individual Queue Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {Object.entries(queueStatuses).map(([queueName, status]) => {
          const queueHealth = getHealthStatus(status.health);
          return (
            <div
              key={queueName}
              className={`p-4 rounded-lg border ${
                theme === 'dark' 
                  ? 'bg-gray-700 border-gray-600' 
                  : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className={`text-sm font-medium ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  {queueName.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </h4>
                <span className="text-lg">{queueHealth.icon}</span>
              </div>
              
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                    Depth
                  </span>
                  <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                    {status.depth || 0}
                  </span>
                </div>
                
                <div className="flex justify-between text-xs">
                  <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                    Processing
                  </span>
                  <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                    {status.processingTime || 0}ms
                  </span>
                </div>
                
                <div className="flex justify-between text-xs">
                  <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                    Error Rate
                  </span>
                  <span className={`${
                    (status.errorRate || 0) > 0.05 ? 'text-red-500' : 'text-green-500'
                  }`}>
                    {((status.errorRate || 0) * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Active Alerts */}
      {alerts.length > 0 && (
        <div className="mb-6">
          <h4 className={`text-sm font-medium mb-3 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Active Alerts ({alerts.length})
          </h4>
          
          <div className="space-y-2">
            {alerts.slice(0, 5).map((alert, index) => {
              const alertSeverity = getHealthStatus(alert.severity);
              return (
                <div
                  key={index}
                  className={`flex items-start p-3 rounded-lg border ${
                    theme === 'dark' 
                      ? 'bg-gray-700 border-gray-600' 
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <span className="text-lg mr-3">{alertSeverity.icon}</span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <p className={`text-sm font-medium ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>
                        {alert.title}
                      </p>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        alertSeverity.bgColor
                      } ${alertSeverity.color}`}>
                        {alert.severity}
                      </span>
                    </div>
                    <p className={`text-sm mt-1 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {alert.message}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <span className={`text-xs ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        Queue: {alert.queueName}
                      </span>
                      <span className={`text-xs ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        {new Date(alert.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {alerts.length > 5 && (
              <div className={`text-center py-2 text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                ... and {alerts.length - 5} more alerts
              </div>
            )}
          </div>
        </div>
      )}

      {/* System Metrics Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="text-center">
          <div className={`text-2xl font-bold ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            {data.totalMessages?.toLocaleString() || 0}
          </div>
          <div className={`text-sm ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Total Messages
          </div>
        </div>
        
        <div className="text-center">
          <div className={`text-2xl font-bold ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            {data.avgProcessingTime || 0}ms
          </div>
          <div className={`text-sm ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Avg Processing
          </div>
        </div>
        
        <div className="text-center">
          <div className={`text-2xl font-bold ${
            (data.successRate || 0) >= 0.95 ? 'text-green-500' : 'text-yellow-500'
          }`}>
            {((data.successRate || 0) * 100).toFixed(1)}%
          </div>
          <div className={`text-sm ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Success Rate
          </div>
        </div>
        
        <div className="text-center">
          <div className={`text-2xl font-bold ${
            (data.totalCost || 0) > 0 ? 'text-blue-500' : theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            ${(data.totalCost || 0).toFixed(2)}
          </div>
          <div className={`text-sm ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Total Cost
          </div>
        </div>
      </div>
    </div>
  );
};

export default QueueHealthIndicator;
